import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getOptimizedSocialImage } from '../../utils/imageUtils';
import { 
  validateJobPostingSchema, 
  logSchemaValidation,
  formatExperienceToMonths,
  formatQualificationToCredential,
  parseLocationToAddress
} from '../../utils/schemaUtils';

const JobPostingSchema = ({ job }) => {
  // Safety check - if job is not available, don't render schema
  if (!job || typeof job !== 'object') {
    console.log('JobPostingSchema: Job object not available');
    return null;
  }

  // Format date to ISO 8601 format required by schema.org
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toISOString(); // Fallback to current date
    try {
      return new Date(dateString).toISOString();
    } catch (error) {
      console.warn('Invalid date format:', dateString);
      return new Date().toISOString();
    }
  };

  // Format salary range with fallbacks for new structure
  const formatSalary = (salary) => {
    if (!salary || typeof salary !== 'object') {
      // Static fallback salary for schema compliance
      return {
        '@type': 'MonetaryAmount',
        currency: 'USD',
        value: {
          '@type': 'QuantitativeValue',
          minValue: 50000,
          maxValue: 100000,
          unitText: 'YEAR'
        }
      };
    }
    
    const periodMap = {
      'yearly': 'YEAR',
      'monthly': 'MONTH',
      'hourly': 'HOUR'
    };
    
    return {
      '@type': 'MonetaryAmount',
      currency: salary.currency || 'USD',
      value: {
        '@type': 'QuantitativeValue',
        minValue: salary.min || 50000,
        maxValue: salary.max || 100000,
        unitText: periodMap[salary.period] || 'YEAR'
      }
    };
  };

  // Get company information from new structure
  const getCompanyInfo = () => {
    if (job.hiringOrganization) {
      return {
        '@type': 'Organization',
        name: job.hiringOrganization.name || 'Company',
        logo: getOptimizedSocialImage(job.hiringOrganization.logo, 'job') || 'https://jobsopportunity.in/logo.png',
        url: job.hiringOrganization.website || 'https://jobsopportunity.in',
        sameAs: job.hiringOrganization.sameAs || 'https://jobsopportunity.in'
      };
    }
    
    // Fallback to old structure
    return {
      '@type': 'Organization',
      name: job.company || 'JobOpportunity.in',
      logo: getOptimizedSocialImage(job.logo, 'job') || 'https://jobsopportunity.in/logo.png',
      sameAs: 'https://jobsopportunity.in',
      url: 'https://jobsopportunity.in'
    };
  };

  // Get location information from new structure
  const getJobLocation = () => {
    if (job.location && typeof job.location === 'object') {
      const address = {
        '@type': 'PostalAddress'
      };
      
      if (job.location.address) address.streetAddress = job.location.address;
      if (job.location.city) address.addressLocality = job.location.city;
      if (job.location.state) address.addressRegion = job.location.state;
      if (job.location.country) address.addressCountry = job.location.country;
      if (job.location.postalCode) address.postalCode = job.location.postalCode;
      
      return {
        '@type': 'Place',
        address: address
      };
    }
    
    // Fallback to old structure
    return {
      '@type': 'Place',
      address: parseLocationToAddress(job.location)
    };
  };

  // Get experience requirements from new structure
  const getExperienceRequirements = () => {
    const experience = job.overview?.experience || job.experienceRequirements || job.experience;
    if (experience) {
      return {
        '@type': 'OccupationalExperienceRequirements',
        monthsOfExperience: formatExperienceToMonths(experience)
      };
    }
    return null;
  };

  // Get education requirements from new structure
  const getEducationRequirements = () => {
    const qualification = job.overview?.qualification || job.educationRequirements || job.qualification;
    if (qualification) {
      return {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: formatQualificationToCredential(qualification)
      };
    }
    return null;
  };

  // Get skills from new structure
  const getSkills = () => {
    if (Array.isArray(job.skills) && job.skills.length > 0) {
      return job.skills.join(', ');
    }
    return 'Communication, Teamwork, Problem Solving';
  };

  // Get employment type from new structure
  const getEmploymentType = () => {
    if (job.employmentType) {
      return job.employmentType;
    }
    if (job.jobType) {
      const typeMap = {
        'full-time': 'FULL_TIME',
        'part-time': 'PART_TIME',
        'contract': 'CONTRACTOR',
        'temporary': 'TEMPORARY',
        'internship': 'INTERN'
      };
      return typeMap[job.jobType] || 'FULL_TIME';
    }
    return 'FULL_TIME';
  };

  // Create the JSON-LD schema with comprehensive fallbacks
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title || 'Job Opportunity',
    description: job.description || job.content || 'Join our team and grow your career with us.',
    datePosted: formatDate(job.datePosted || job.postedDate),
    validThrough: formatDate(job.validThrough || job.applicationDeadline),
    employmentType: getEmploymentType(),
    hiringOrganization: getCompanyInfo(),
    jobLocation: getJobLocation(),
    baseSalary: formatSalary(job.salary),
    gender: job.gender || 'All',
    industry: job.overview?.industry || job.category || 'Technology',
    positionLevel: job.overview?.careerLevel || job.positionLevel || 'Entry Level',
    jobBenefits: job.jobBenefits || (Array.isArray(job.benefits) ? job.benefits : ['Health Insurance', 'Paid Time Off', 'Professional Development']),
    skills: getSkills(),
    workHours: job.workHours || 'Full-time',
    specialCommitments: Array.isArray(job.specialCommitments) ? job.specialCommitments : [],
    // Additional fields for better SEO
    identifier: {
      '@type': 'PropertyValue',
      name: 'Job ID',
      value: job._id || job.id || 'JOB001'
    },
    occupationalCategory: job.category || 'Technology',
    responsibilities: job.responsibilities || 'Perform assigned duties and responsibilities as outlined in the job description.',
    qualifications: job.overview?.qualification || job.qualifications || 'Relevant experience and education as specified in the job requirements.',
    applicationContact: {
      '@type': 'ContactPoint',
      contactType: 'application',
      url: `https://jobsopportunity.in/jobs/${job.slug || 'apply'}`
    }
  };

  // Add experience and education requirements if available
  const experienceReq = getExperienceRequirements();
  if (experienceReq) {
    schema.experienceRequirements = experienceReq;
  }

  const educationReq = getEducationRequirements();
  if (educationReq) {
    schema.educationRequirements = educationReq;
  }

  // Add applicant location requirements if available
  if (job.applicantLocationRequirements) {
    schema.applicantLocationRequirements = {
      '@type': 'Country',
      name: job.applicantLocationRequirements.allowedCountry || 'United States'
    };
  }

  // Add incentive compensation if available
  if (job.jobBenefits) {
    schema.incentiveCompensation = job.jobBenefits;
  }

  // Validate schema and log results
  const validationResult = validateJobPostingSchema(schema);
  logSchemaValidation(validationResult, job.title);

  // Debug logging
  console.log('JobPostingSchema Debug:', {
    jobExists: !!job,
    jobType: typeof job,
    title: job.title,
    company: job.hiringOrganization?.name || job.company,
    location: job.location,
    experience: job.overview?.experience || job.experience,
    qualification: job.overview?.qualification || job.qualification,
    salary: job.salary,
    employmentType: getEmploymentType(),
    schemaValid: validationResult.isValid,
    schemaErrors: validationResult.errors.length,
    schemaWarnings: validationResult.warnings.length
  });

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default JobPostingSchema; 